
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { convertToUTCDateTime } from "@/utils/common";
import {
  getTodayMidnightLocal,
  getYesterDayMidnightLocal,
} from "@/features/dashboard/utils";
import { useGetDailySellQuery } from "@/features/order";
import { currencyFormatter } from "@/utils";
import { useGetTotalStockValueQuery } from "@/features/seller";
import { DashboardCard } from "@/components/ui/dashboard";
import { currentUserSelector } from "@/features/auth";
import { useSelector } from "react-redux";
import { StoreRoleEnum } from "@/enum";
import { PaymentMethodCard } from "@/components/ui/dashboard/PaymentMethodCard";

import { useGetCategoryListQuery } from "@/features/category";
import { ProfitCardWithPercentage } from "@/components/ui/dashboard/ProfitCardWithPercentage";
import { LastMonthCard, ThisMonthCard } from "@/components/ui/dashboard/DashboardCard";

export const Route = createFileRoute("/(app)/dashboard/")({
  component: RouteComponent,
});

function RouteComponent() {
  const todayQuery = useMemo(
    () => "from-date=" + convertToUTCDateTime(getTodayMidnightLocal()),
    []
  );

  const yesterdayQuery = useMemo(
    () =>
      "from-date=" +
      convertToUTCDateTime(getYesterDayMidnightLocal()) +
      "&to-date=" +
      convertToUTCDateTime(getTodayMidnightLocal()),
    []
  );
  const navigate = useNavigate();
  const { data }: any = useGetDailySellQuery(todayQuery as any);
  const { data: yesterdayData } = useGetDailySellQuery(yesterdayQuery as any);

  const todaySales = data?.at(0)?.totalSales || 0;
  const todayExpenses = data?.at(0)?.expenseSummary?.totalAmount || 0;
  const todayProductProfit = data?.at(0)?.totalProductProfit || 0;
  const dailyProfit = todayProductProfit - todayExpenses;
  const paymentMethodTotals = data?.at(0)?.paymentMethodTotals;
  
  const yesterdaySales = yesterdayData?.at(0)?.totalSales || 0;
  const yesterdayExpenses = yesterdayData?.at(0)?.expenseSummary?.totalAmount || 0;
  const yesterdayProductProfit = yesterdayData?.at(0)?.totalProductProfit || 0;
  const yesterdayProfit = yesterdayProductProfit - yesterdayExpenses;
  const todayOrders = data?.at(0)?.totalOrders || 0;
  const yesterdayOrders = yesterdayData?.at(0)?.totalOrders || 0;
  const currentUser = useSelector(currentUserSelector);

  // const [getProductCount] = useLazyGetStoreProductCountQuery();
  const { data: totalStockValue } = useGetTotalStockValueQuery(undefined);
  const { data: expenseCategoryList } = useGetCategoryListQuery({
    type: "Service",
    limit: 20,
  });

  // useEffect(() => {
  //   const fetchProductCount = async () => {
  //     try {
  //       await getProductCount(null).unwrap();
  //     } catch (error) {
  //       console.error("Failed to fetch product count:", error);
  //     }
  //   };
  //   fetchProductCount();
  // }, [getProductCount]);

  const calculateComparison = (today: number, yesterday: number) => {
    if (today === 0 && yesterday === 0) {
      return { percentage: 0, isIncrease: false, text: "No change" };
    }
    if (yesterday === 0 && today > 0) {
      return { percentage: Infinity, isIncrease: true, text: "New growth" };
    }
    if (today === 0 && yesterday > 0) {
      return { percentage: -100, isIncrease: false, text: "100% decrease" };
    }

    const percentage = +(((today - yesterday) / yesterday) * 100).toFixed(2);
    return {
      percentage,
      isIncrease: percentage > 0,
      text: `${Math.abs(percentage)}% ${percentage > 0 ? "increase" : "decrease"} from yesterday`,
    };
  };

  // Calculate profit margin (profit / sales)
  const calculateProfitMargin = (profit: number, sales: number) => {
    if (sales === 0 || !sales) return 0;
    return +((profit / sales) * 100).toFixed(2);
  };

  const todayProfitPercentage = calculateProfitMargin(dailyProfit, todaySales);

  const salesComparison = calculateComparison(todaySales, yesterdaySales);
  const profitComparison = calculateComparison(dailyProfit, yesterdayProfit);
  const orderComparison = calculateComparison(todayOrders, yesterdayOrders);

  const handleExpenseCategoryClick = (category: any) => {
    navigate({
      to: '/expense/$expenseCategoryId',
      params: { expenseCategoryId: category.name },
      state: { expenseCategory: category }
    });
  };

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="p-8">
        <div className="pb-6">
          <DashboardCard
            title="Total Stock"
            graph={false}
            digit={currencyFormatter(
              totalStockValue?.data?.totalStockValue ?? 0
            )}
            subText="Stocks"
            description={`Total Quantity: ${totalStockValue?.data?.totalQuantity ?? 0}`}
          />
        </div>
        {[
          StoreRoleEnum.Admin,
          StoreRoleEnum.Owner,
          StoreRoleEnum.Manager,
        ].includes(currentUser?.userRole) ? (
          <div className="flex flex-wrap overflow-x-auto gap-6 pb-4">
            <DashboardCard
              title="Sale"
              graph={true}
              isUp={salesComparison.isIncrease}
              digit={currencyFormatter(todaySales)}
              subText="Total Sales"
              changePercent={salesComparison.percentage}
            />
            {!!paymentMethodTotals && (
              <PaymentMethodCard
                paymentMethodTotals={paymentMethodTotals}
                title="Today's Payment Methods"
              />
            )}
            {currentUser?.userRole !== StoreRoleEnum.Manager && (
              <>
                <ProfitCardWithPercentage
                  title="Profit"
                  graph={true}
                  isUp={profitComparison.isIncrease}
                  profitAmount={currencyFormatter(dailyProfit)}
                  profitPercentage={todayProfitPercentage}
                  subText="Total Earnings"
                  changePercent={profitComparison.percentage}
                />
                <DashboardCard
                  title="Total Orders"
                  graph={true}
                  isUp={orderComparison.isIncrease}
                  digit={todayOrders}
                  subText="Today's Orders"
                  changePercent={orderComparison.percentage}
                />
                <ThisMonthCard />
                <LastMonthCard />
              </>
            )}

            <div className="w-full bg-surfaceBase rounded-lg shadow-md p-4">
              <h3 className="text-lg font-semibold w-full pb-1">Expense</h3>
              <div className="flex flex-wrap gap-5">
                {expenseCategoryList?.map((category) => (
                  <button
                    onClick={() => handleExpenseCategoryClick(category)}
                    key={category._id}
                    className="flex flex-col items-center justify-center rounded-lg hover:shadow-md cursor-pointer w-24"
                  >
                    <img src={category.icon} className="w-20 h-20" alt="img" />
                    <span className="text-[14px] text-center">
                      {category.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ margin: "1rem" }}>
            You don't have permission to perform other action
          </div>
        )}
      </div>
    </div>
  );
}